import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Mail, ArrowLeft, CheckCircle } from "lucide-react";
import { useAdminAuth } from "../services/admin/AdminAuthServices";
import { useNotification } from "../context/NotificationContext";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState(1); // 1: Email, 2: OTP
  const [countdown, setCountdown] = useState(0);

  const { forgotPasswordAPI, verifyOtpAPI, loading: isLoading } = useAdminAuth();


  // Countdown timer for resend button
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!email) {
      showNotification({
        type: "failed",
        title: "Input Required",
        message: "Please enter your email address."
      });
      return;
    }

    const res = await forgotPasswordAPI(email);
    if (res.status === "success") {
      setStep(2);
      setCountdown(60);
    }
  };

  const handleResendOTP = async () => {
    const res = await forgotPasswordAPI(email);
    if (res.status === "success") {
      setCountdown(60);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otp) {
      showNotification({
        type: "failed",
        title: "Input Required",
        message: "Please enter the OTP."
      });
      return;
    }

    const res = await verifyOtpAPI(email, otp);
    if (res && res.status === "success") {
      showNotification({
        type: "success",
        title: "Verified",
        message: "OTP verified. Proceeding to reset password..."
      });
      // Redirect to password reset page
      setTimeout(() => {
        navigate(`/admin/reset-password?email=${encodeURIComponent(email)}&otp=${otp}`);
      }, 1500);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFDFD] dark:from-[#0A0A0A] dark:via-[#111111] dark:to-[#1A1A1A] flex items-center justify-center p-4">
      {/* Background patterns */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-[#8B5E3C]/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-[#8B5E3C]/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="w-full max-w-md relative">
        {/* Back Button */}
        <Link
          to="/admin/login"
          className="inline-flex items-center gap-2 text-[#8B5E3C] hover:text-[#111111] mb-8 font-inter font-bold uppercase tracking-wider text-xs transition-colors"
        >
          <ArrowLeft size={16} />
          Back to Login
        </Link>

        {/* Logo and Brand */}
        <div className="text-center mb-10 animate-fade-in">
          <div className="w-20 h-20 bg-white border border-[#E5E7EB] rounded-2xl flex items-center justify-center shadow-sm mx-auto mb-6 transform hover:scale-105 transition-transform duration-300 overflow-hidden">
            <img src="/static/images/temb_music_logo_transparent.png" alt="Temidayo Balogun" className="w-full h-full object-contain p-2" loading="eager" />
          </div>
          <h1 className="text-3xl font-normal text-[#111111] dark:text-white mb-2" style={{ fontFamily: "Playfair Display, Georgia, serif" }}>
            Forgot Password
          </h1>
          <p className="text-black dark:text-[#A0A0A0] font-inter uppercase tracking-[2px] text-xs font-semibold">
            {step === 1
              ? "Identify yourself to reset"
              : "Verify your identity"}
          </p>
        </div>

        {/* Card */}
        <div className="bg-white dark:bg-[#1A1A1A] rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.05)] border border-[#F0F0F0] dark:border-[#2A2A2A] p-8 md:p-10">
          {/* Step 1: Email Input */}
          {step === 1 && (
            <form onSubmit={handleSendOTP} className="space-y-6">

              {/* Email Field */}
              <div className="space-y-2">
                <label className="block text-xs font-bold uppercase tracking-wider text-[#111111] dark:text-white font-inter">
                  Email Address
                </label>
                <div className="relative">
                  <Mail
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 text-black dark:text-[#A0A0A0]"
                    size={16}
                  />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-[#F9FAFB] dark:bg-[#0A0A0A] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-xl text-[#111111] dark:text-white placeholder-black/60 dark:placeholder-[#666666] font-inter focus:outline-none focus:ring-1 focus:ring-[#8B5E3C] focus:border-[#8B5E3C] transition-all duration-200"
                    placeholder="admin@temidayobalogun.com"
                    required
                  />
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-4 px-6 bg-[#111111] hover:bg-[#8B5E3C] text-white font-bold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.01] active:scale-[0.99] transition-all duration-300 font-inter uppercase tracking-[2px] text-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Sending...
                  </span>
                ) : (
                  "Send Code"
                )}
              </button>
            </form>
          )}

          {/* Step 2: OTP Input */}
          {step === 2 && (
            <div className="space-y-6">
              {/* Success Message */}
              <div className="bg-[#8B5E3C]/5 dark:bg-green-900/20 border border-[#8B5E3C]/10 dark:border-green-800 rounded-xl p-4 animate-fade-in">
                <div className="flex items-start gap-3">
                  <CheckCircle
                    className="text-[#8B5E3C] dark:text-green-400 flex-shrink-0 mt-0.5"
                    size={20}
                  />
                  <div>
                    <h3 className="text-[#111111] dark:text-green-300 font-bold mb-1 font-inter uppercase tracking-wider text-xs">
                      Code Sent
                    </h3>
                    <p className="text-black dark:text-green-400 text-xs font-inter leading-relaxed">
                      Verification code sent to<br />
                      <span className="font-bold text-[#111111]">{email}</span>
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleVerifyOTP} className="space-y-6">

                {/* OTP Field */}
                <div className="space-y-2">
                  <label className="block text-xs font-bold uppercase tracking-wider text-[#111111] dark:text-white font-inter">
                    Enter Code
                  </label>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) =>
                      setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    className="w-full px-4 py-3 bg-[#F9FAFB] dark:bg-[#0A0A0A] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-xl text-[#111111] dark:text-white text-center text-3xl tracking-[10px] placeholder-black/40 dark:placeholder-[#BBBBBB] font-mono focus:outline-none focus:ring-1 focus:ring-[#8B5E3C] focus:border-[#8B5E3C] transition-all duration-200"
                    placeholder="000000"
                    maxLength={6}
                    required
                  />
                </div>

                {/* Resend Button */}
                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    disabled={countdown > 0 || isLoading}
                    className="text-xs text-[#8B5E3C] hover:text-[#111111] font-bold uppercase tracking-widest disabled:opacity-40 transition-colors"
                  >
                    {countdown > 0
                      ? `Resend in ${countdown}s`
                      : "Resend Code"}
                  </button>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isLoading || otp.length !== 6}
                  className="w-full py-4 px-6 bg-[#111111] hover:bg-[#8B5E3C] text-white font-bold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.01] active:scale-[0.99] transition-all duration-300 font-inter uppercase tracking-[2px] text-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Verifying...
                    </span>
                  ) : (
                    "Verify Code"
                  )}
                </button>

                {/* Change Email */}
                <button
                  type="button"
                  onClick={() => {
                    setStep(1);
                    setOtp("");
                  }}
                  className="w-full text-xs text-black hover:text-[#111111] font-bold uppercase tracking-widest transition-colors"
                >
                  Edit Email
                </button>
              </form>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-black dark:text-[#666666] mt-10 font-inter uppercase tracking-widest">
          &copy; {new Date().getFullYear()} TEMB Music. All rights reserved.
        </p>
      </div>

      <style jsx global>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }
      `}</style>
    </div>
  );
}
